import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '@/components/base/LanguageSelector';

const getNavItems = (t: (k: string) => string) => [
  { label: t('nav_home'), href: '#home' },
  { label: t('nav_services'), href: '#services' },
  { label: t('nav_gallery'), href: '#gallery' },
  { label: t('nav_areas'), href: '#areas' },
  { label: t('nav_reviews'), href: '#reviews' },
  { label: t('nav_contact'), href: '#contact' },
];

export default function Header() {
  const { t } = useTranslation('home');
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navItems = getNavItems(t);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6 lg:px-24 max-w-7xl mx-auto h-16 md:h-[70px] flex items-center justify-between">
        {/* Logo */}
        <a href="#home" onClick={(e) => handleNavClick(e, '#home')} className="flex items-center gap-2 md:gap-3">
          <img
            src="https://static.readdy.ai/image/e50d925263add9c47b947a5709171c8c/d305b02287d3fda18a9e9b5913e18fa7.png"
            alt="Complete Home Care Cleaning logo"
            className="h-8 md:h-10 w-auto object-contain"
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-5 lg:gap-8">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className={`text-sm font-medium transition-colors relative group ${
                scrolled
                  ? 'text-brand-dark hover:text-brand-mint'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              {item.label}
              <span
                className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-brand-mint transition-all duration-300 group-hover:w-full`}
              />
            </a>
          ))}
        </nav>

        {/* CTA + Language */}
        <div className="hidden md:flex items-center gap-3">
          <LanguageSelector variant="header" scrolled={scrolled} />
          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, '#contact')}
            className="inline-flex items-center px-5 py-2.5 bg-brand-dark text-white text-sm font-semibold rounded-full hover:bg-brand-mint transition-colors whitespace-nowrap"
          >
            {t('nav_cta')}
          </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer"
          aria-label={t('admin_toggle_menu')}
        >
          <i
            className={`text-xl ${scrolled ? 'text-brand-dark' : 'text-white'} ${
              mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'
            }`}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-16 left-0 right-0 bg-white shadow-lg border-t border-gray-100 transition-all duration-300 overflow-hidden ${
          mobileOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="flex flex-col px-4 py-4 gap-1">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className="text-sm font-medium text-brand-dark hover:text-brand-mint hover:bg-brand-light px-4 py-3 rounded-lg transition-colors"
            >
              {item.label}
            </a>
          ))}
          <div className="px-4 py-2">
            <LanguageSelector variant="admin" />
          </div>
          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, '#contact')}
            className="mt-2 inline-flex items-center justify-center px-5 py-3 bg-brand-dark text-white text-sm font-semibold rounded-full hover:bg-brand-mint transition-colors whitespace-nowrap"
          >
            {t('nav_cta')}
          </a>
        </nav>
      </div>
    </header>
  );
}